import React, { useContext, useCallback, useEffect } from 'react';
import { Typography, Grid, Paper, Icon, CircularProgress, makeStyles, Theme } from '@material-ui/core';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { fetchProjectStatistics, ritzDataContext, selectProjectById, selectProjectStatistics } from '@moderepo/mode-data-state-ritz';
import { capitalizeAll } from '@moderepo/mode-app-components';
import { useRitzTranslation } from '../../utils/customHooks';
import { RouteHelper } from '../routes/RouteHelper';
import { RitzComponentsCollection } from '../../utils/ritzComponentsCollection';



const useStyle = makeStyles((theme: Theme) => {
    return {
        dashboard: {
        },

        root: {
            marginTop: theme.spacing(1),
        },

        dashboardItem: {
            marginTop                   : theme.spacing(1),
            marginBottom                : theme.spacing(1),
            position                    : 'relative',
            overflow                    : 'visible',
            [theme.breakpoints.up('xl')]: {
                marginTop: theme.spacing(0),
            },
            '&.clickable': {
                cursor: 'pointer',
            },
        },

        dashboardItemIconContainer: {
            position                    : 'absolute',
            zIndex                      : 2,
            width                       : 60,
            height                      : 60,
            top                         : theme.spacing(-0.5),
            left                        : theme.spacing(3),
            display                     : 'flex',
            alignItems                  : 'center',
            justifyContent              : 'center',
            boxShadow                   : '0 4px 20px 0 rgba(0, 0, 0,.14), 0 7px 10px -5px rgba(255, 152, 0,.4)',
            [theme.breakpoints.up('sm')]: {
                width : 70,
                height: 70,
            },
        },


        dashboardItemIcon: {
            fontSize                    : 30,
            textShadow                  : '2px 2px 5px rgba(0, 0, 0, 0.2)',
            [theme.breakpoints.up('sm')]: {
                fontSize: 40,
            },
        },

        dashboardItemContent: {
            zIndex        : 1,
            textAlign     : 'end',
            padding       : theme.spacing(1.5),
            position      : 'relative',
            overflow      : 'visible',
            '&:last-child': {
                paddingBottom: theme.spacing(1.5),
            },
        },

        dashboardIssues: {
            '& .icon-container': {
                background: 'linear-gradient(60deg, #ef5350, #e53935)',
                color     : '#ffffff',
            },
        },

        dashboardUsers: {
            '& .icon-container': {
                background: 'linear-gradient(60deg, #66bb6a, #43a047)',
                color     : '#ffffff',
            },
        },

        dashboardHomes: {
            '& .icon-container': {
                background: 'linear-gradient(60deg, #ffa726, #fb8c00)',
                color     : '#fff',
            },
        },

        dashboardDevices: {
            '& .icon-container': {
                background: 'linear-gradient(60deg, #26c6da, #00acc1)',
                color     : '#fff',
            },
        },


        dashboardItemTitle: {
            fontSize                    : 14,
            [theme.breakpoints.up('sm')]: {
                fontSize: 18,
            },
        },

        dashboardItemValue: {
            textShadow                  : '2px 2px 5px rgba(0, 0, 0, 0.2)',
            fontSize                    : '30px',
            [theme.breakpoints.up('sm')]: {
                fontSize: '35px',
            },
        },

        dashboardItemActionContainer: {
            margin   : theme.spacing(0, 1.5, 0, 1.5),
            padding  : theme.spacing(1, 0),
            borderTop: '1px solid #eee',
        },

    };
}, { name: 'ProjectStatisticsContainer' });


export interface ProjectStatisticsContainerProps {
    readonly projectId: number;
}


/**
 * This is the page where we show the project details such as dashboard, agents, etc...
 */
export const ProjectStatisticsContainer: React.FC<ProjectStatisticsContainerProps> = (props: ProjectStatisticsContainerProps) => {
    const navigate = useNavigate();
    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const project = selectProjectById(dataState, props.projectId);
    const projectStats = selectProjectStatistics(dataState, props.projectId);
    const { trans } = useRitzTranslation(project);
    const classes = useStyle();
    const routeHelper = RouteHelper.getInstance(navigate);



    useEffect(() => {
        dataDispatch(fetchProjectStatistics(props.projectId)).catch(() => { /* ignore error */ });
    }, [dataDispatch, props.projectId]);



    /**
     * Create ONE dashboard item for displaying project's statistic
     */
    const createProjectStatisticDashboardItem = useCallback((
        className: string, icon: string, title: string, value: number, onClick?: ()=> void,
    ): JSX.Element => {
        return (
            <Grid
                className={clsx(classes.dashboardItem, className, onClick && 'clickable')}
                item
                xs={12}
                sm={6}
                lg={3}
                onClick={onClick}
            >
                <Paper className={clsx(classes.dashboardItemIconContainer, 'icon-container')}>
                    <Icon className={classes.dashboardItemIcon}>{icon}</Icon>
                </Paper>
                <Paper elevation={2}>
                    <div className={classes.dashboardItemContent}>
                        <Typography className={classes.dashboardItemTitle} variant="h6" color="textSecondary" gutterBottom>
                            {trans(title)}
                        </Typography>
                        <Typography className={classes.dashboardItemValue} variant="h4" color="textSecondary">
                            {trans('{{value, number-format}}', { value })}
                        </Typography>
                    </div>
                </Paper>
            </Grid>
        );
    }, [classes, trans]);



    /**
     * On Devices dashboard item clicked, take the user to the Issues page
     */
    const onDevicesDashboardItemClicked = useCallback(() => {
        routeHelper.gotoProjectDevicesPage(props.projectId);
    }, [routeHelper, props.projectId]);


    /**
     * On Customers dashboard item clicked, take the user to the Issues page
     */
    const onCustomersDashboardItemClicked = useCallback(() => {
        routeHelper.gotoUsersPage(props.projectId);
    }, [routeHelper, props.projectId]);


    /**
     * On Customers dashboard item clicked, take the user to the Issues page
     */
    const onHomesDashboardItemClicked = useCallback(() => {
        routeHelper.gotoHomesPage(props.projectId);
    }, [routeHelper, props.projectId]);


    return (
        <>
            {project ? (
                <Grid className={classes.root} item container spacing={3} xs={12}>
                    {createProjectStatisticDashboardItem(
                        classes.dashboardIssues,
                        'priority_high',
                        capitalizeAll(trans('label.issue', { count: 2 })), 0,
                    )}
                    {createProjectStatisticDashboardItem(
                        classes.dashboardUsers,
                        trans('icons.user'),
                        capitalizeAll(trans('terms.user', { count: 2 })),
                        projectStats && projectStats.users ? projectStats.users.verified + projectStats.users.unverified : 0,
                        onCustomersDashboardItemClicked,
                    )}
                    {createProjectStatisticDashboardItem(
                        classes.dashboardHomes,
                        trans('icons.home'),
                        capitalizeAll(trans('terms.home', { count: 2 })),
                        projectStats && projectStats.homes ? projectStats.homes.all : 0,
                        onHomesDashboardItemClicked,
                    )}
                    {createProjectStatisticDashboardItem(
                        classes.dashboardDevices,
                        trans('icons.device'),
                        capitalizeAll(trans('terms.device', { count: 2 })),
                        projectStats && projectStats.devices
                            ? Object.values(
                                projectStats.devices,
                            ).reduce((sum: number, device: {claimed: number, unclaimed: number}): number => {
                                return sum + device.claimed + device.unclaimed;
                            }, 0) : 0,
                        onDevicesDashboardItemClicked,
                    )}
                </Grid>
            ) : (
                <CircularProgress />
            )}
        </>
    );
};


// Register this component with the RitzComponentsCollection so that it can be used by the Custom UI system
RitzComponentsCollection.getInstance().registerComponent('ProjectStatisticsContainer', ProjectStatisticsContainer);
